import { Injectable, inject } from '@angular/core';
import { SupabaseClient } from '@supabase/supabase-js';
import { User } from '@app/model/user';
import { Notification } from '@app/model/notification';
import { UserService } from '../user-service/user.service';
import { SUPABASE_CLIENT } from '@app/providers/supabase.provider';

type CreateNotificationOptions = {
  waitForPush?: boolean;
  authorizationTaskIds?: string[];
};

@Injectable({
  providedIn: 'root'
})
export class NotificationService {

  user: User = new User('', '', '', '', '', '', '', 0, 0, 0, '', false, false, false, 0, '', '', '');
  notification: Notification = new Notification('', '', '', '', '', false, '', '');
  userId: any;

  private supabaseClient: SupabaseClient;

  constructor(private userService: UserService) {
    this.supabaseClient = inject(SUPABASE_CLIENT);
  }

  async getAllUserId() {
    const res = await this.userService.getAllUser();
    return (res?.data ?? [])
      .map((user: User) => user.id)
      .filter((id: string | undefined): id is string => Boolean(id));
  }

  async getNotificationByUserId() {
    await this.userService.getUser()?.then((res) => {
      if (res) {
        this.userId = res?.data?.[0].id;
      }
    });

    const res = await this.supabaseClient.from('notification').select('*').eq('sent_to', this.userId);
    return res;
  }

  async createNotification(title: string, description: string, getterId: string, notificationType: string, boundIdForNotification: string) {
    const { error } = await this.supabaseClient.functions.invoke('create-notification', {
      body: {
        title,
        description,
        sentTo: getterId,
        notificationType,
        boundId: boundIdForNotification
      }
    });

    if (error) {
      console.error('建立通知失敗:', error);
      throw error;
    }
  }

  async createNotifications(
    title: string,
    description: string,
    getterIds: string[],
    notificationType: string,
    boundIdForNotification: string,
    options: CreateNotificationOptions = {}
  ) {
    const sentTo = [...new Set(getterIds.filter(Boolean))];
    if (sentTo.length === 0) {
      return { created: 0 };
    }

    const { data, error } = await this.supabaseClient.functions.invoke('create-notification', {
      body: {
        title,
        description,
        sentTo,
        notificationType,
        boundId: boundIdForNotification,
        waitForPush: options.waitForPush ?? false,
        authorizationTaskIds: options.authorizationTaskIds ?? []
      }
    });

    if (error) {
      console.error('批次建立通知失敗:', error);
      throw error;
    }

    return data as { created?: number; push?: unknown } | null;
  }

  async readNotification(notification: Notification) {
    await this.supabaseClient.from('notification').delete().eq('id', notification.id);
  }

  async makeAnnouncementNotificationForCreateTask(title: string, description: string, notificationType: string, taskId: string) {
    const userIds = await this.getAllUserId();
    return await this.createNotifications(title, description, userIds, notificationType, taskId);
  }

  async makeAnnouncementNotification(title: string, description: string, notificationType: string, boundIdForNotification: string, options: CreateNotificationOptions = {}) {
    const userIds = await this.getAllUserId();
    return await this.createNotifications(title, description, userIds, notificationType, boundIdForNotification, options);
  }

  async makeCustomAnnouncementNotification(title: string, description: string, usersId: string[], notificationType: string, boundIdForNotification: string, options: CreateNotificationOptions = {}) {
    return await this.createNotifications(title, description, usersId, notificationType, boundIdForNotification, options);
  }

  async makeCustomAnnouncementNotificationForCreateTask(title: string, description: string, usersId: string[], notificationType: string, taskId: string) {
    return await this.createNotifications(title, description, usersId, notificationType, taskId);
  }

  async tryCreateNotification(title: string, description: string, getterId: string, notificationType: string, boundIdForNotification: string) {
    try {
      return await this.createNotification(title, description, getterId, notificationType, boundIdForNotification);
    } catch (error) {
      console.error('通知建立失敗，已略過:', error);
      return null;
    }
  }

  async tryCreateNotifications(
    title: string,
    description: string,
    getterIds: string[],
    notificationType: string,
    boundIdForNotification: string,
    options: CreateNotificationOptions = {}
  ) {
    try {
      return await this.createNotifications(
        title,
        description,
        getterIds,
        notificationType,
        boundIdForNotification,
        options
      );
    } catch (error) {
      console.error('批次通知建立失敗，已略過:', error);
      return null;
    }
  }

  async tryMakeAnnouncementNotification(title: string, description: string, notificationType: string, boundIdForNotification: string, options: CreateNotificationOptions = {}) {
    try {
      return await this.makeAnnouncementNotification(title, description, notificationType, boundIdForNotification, options);
    } catch (error) {
      console.error('公告通知建立失敗，已略過:', error);
      return null;
    }
  }

  async tryMakeCustomAnnouncementNotification(title: string, description: string, usersId: string[], notificationType: string, boundIdForNotification: string, options: CreateNotificationOptions = {}) {
    try {
      return await this.makeCustomAnnouncementNotification(title, description, usersId, notificationType, boundIdForNotification, options);
    } catch (error) {
      console.error('自訂公告通知建立失敗，已略過:', error);
      return null;
    }
  }

  async deleteAllNotification(userId: any) {
    await this.supabaseClient.from('notification').delete().eq('sent_to', userId);
  }

  async deleteNotificationByBoundTaskId(taskId: string) {
    await this.supabaseClient.from('notification').delete().eq('bound_id', taskId);
  }

  async deleteNotificationsByBoundTaskIds(taskIds: string[]) {
    const uniqueTaskIds = [...new Set(taskIds.filter(Boolean))];
    if (uniqueTaskIds.length === 0) {
      return;
    }

    const { error } = await this.supabaseClient
      .from('notification')
      .delete()
      .in('bound_id', uniqueTaskIds);

    if (error) {
      throw error;
    }
  }

}
